// @ts-strict-ignore
import type { WorkflowNode } from 'flow/service/graph';

import type {
  CanvasProperties,
  NodeConnectorInteractionState,
  Snapshot,
  SnippetActiveOperation,
  SnippetMode,
  WorkflowHistoryEntry,
  WorkflowSnapshots,
} from './types';
import { EditorContainerType } from './constants';
import type {
  CreateMatrixModelActionPayload,
  CreateOutcomeActionPayload,
  CreateServiceActionPayload,
  CreateTagActionPayload,
  CreateWorkflowRoutingActionPayload,
} from '../engine/create/actions/types';
import type {
  NodeEditorTab, SchemaInstance, WorkflowProps, WorkflowFeatures,
  WorkflowOperationMessage,
  RuleBuilderSubEditorSide,
  RuleBuilderSubEditorType,
  Action,
  Category,
  DataRoute,
  PendingParenthesisInfo,
} from '../engine/types';
import type { FeatureFlags } from '~/helpers/typescript';
import type { RuleBuilderRule } from 'flow/service/rules';
import type { SnippetMetadata } from 'flow/service/snippets';


export type WorkflowState = {
  /**
   * Tracks action & threshold id mappings between the legacy and new editors.
   * Only read from when loading the store outside of the new editor.
   */
  legacyMappings: {
    actions: Record<string, string>;
    thresholds: Record<string, string>;
  };
  // Representation of the workflow configuration
  workflowData?: Partial<SchemaInstance>;
  // Variable storage for individual product offerings
  workflowFeatures?: WorkflowFeatures;
  // Customer props such as tags, outcomes etc (outside of the workflow version)
  workflowProps?: WorkflowProps;
  // List of workflow snapshots to use for undo/redo
  workflowSnapshots: WorkflowSnapshots;
  workflowOperationDescriptions: WorkflowOperationMessage[];
  // State related to the UI of the page (component state, etc.)
  ui: {
    isInDiffMode: boolean;
    activeNodeEditorTab: NodeEditorTab | null;
    isTagSwapMode: { isActive: boolean };
    canvasProperties: CanvasProperties;
    connector: {
      interactionState: NodeConnectorInteractionState;
      ownerNodeId: WorkflowNode['id'];
      canCreateItem: boolean;
    };
    editorContainerType: EditorContainerType;
    shouldRenderCriticalNodePath: boolean;
    floatedAnchorNodeId: WorkflowNode['id'];
    focusedNodeId: WorkflowNode['id'];
    highlightedNodeId: WorkflowNode['id'];
    hoveredNodeId: WorkflowNode['id'];
    components: {
      services: {
        serviceRoutingCategoryNameInEditing: string;
        serviceActionOptionPublicIdInEditing: string;
      };
    };
    // Session-only; outside `components` so it survives resetComponentState.
    deactivatedLegacyDataRoutes: {
      mode: 'incoming' | 'outgoing';
      category: Category;
      dataRoute: DataRoute;
    }[];
    search: {
      isSearchBoxVisible: boolean;
      query: string;
      matchIndex: number;
    };
    nodeContainers: {
      inputAttribute: {
        isCollapsed: boolean;
      };
      snippets: Record<Action['publicId'], { isCollapsed: boolean }>;
    };
    ruleBuilder: {
      // there are issues with combining a drag trigger to also be a dropdown menu trigger
      // so we manually track the open dropdown, scoped by action
      activeRuleNumberDropdown: {
        instanceId: string;
        index: number;
      } | null;
      isDragging: boolean;
      // responsible for the yellow highlighting over rule content
      highlightedRuleId: RuleBuilderRule['id'] | null;
      // responsible for the greyed out rule line highlighting
      hoveredRuleIndex: {
        instanceId: string;
        index: number;
      } | null;
      pendingGroup: {
        parentRuleId: RuleBuilderRule['id'] | null;
        startIndex: number | null;
      };
      // container width for calculating rule item length limits
      panelBoxContainerWidth: number;
      activeSubEditor: {
        thresholdId: RuleBuilderRule['id'] | null;
        type: RuleBuilderSubEditorType | null;
        side: RuleBuilderSubEditorSide | null;
      };
    };
    expressionBuilder: {
      pendingGroup: PendingParenthesisInfo;
    };
    snippet: {
      mode: SnippetMode | null;
      /** Current snippet operation and its context */
      activeOperation: SnippetActiveOperation;
      /** The snapshot id that maps to the pre snippet action (insertion, preview) WorkflowData snapshot */
      preSnippetWorkflowDataSnapshotId: null | string;
      /** The state of workflow props before a snippet action (insertion, preview) */
      preSnippetWorkflowPropsSnapshot: Snapshot<WorkflowProps> | null;
      incompatibleActions: Action[];
    };
  };
  /** only populated if opened on a snippet */
  snippet: {
    metadata: SnippetMetadata | null;
  };
  linkedChildAction: {
    createActionPayload: CreateMatrixModelActionPayload
    | CreateOutcomeActionPayload
    | CreateServiceActionPayload
    | CreateTagActionPayload
    | CreateWorkflowRoutingActionPayload;
    ownerNodeId: WorkflowNode['id'];
    thresholdIds: string[];
  };
  locking: {
    isLocked: boolean;
    isLoggedInAgentOwner: boolean;
    ownerEmail: string;
    ownerFirstName: string;
    ownerLastName: string;
  };
  // This is for adding relevant feature flags to the redux store,
  // specifically utilized for selectors/reducers/thunks (not components)
  featureFlagsForStore?: Pick<FeatureFlags,
  'allowBestEffortRuleEvaluations'
  | 'customTransactionAttributes'
  | 'dynamicDataRouting'
  | 'enableSelfServiceMatchingUi'
  | 'enableWorkflowDiff'
  | 'useTyphonWorkflowEndpoints'
  >;
  editHistory: {
    initial: {
      workflowData: Partial<SchemaInstance> | null;
      workflowProps: WorkflowProps | null;
    };
    history: WorkflowHistoryEntry[];
    historyIndex: number;
  };
};

export const initialState: WorkflowState = {
  legacyMappings: {
    actions: {},
    thresholds: {},
  },
  workflowData: null,
  workflowProps: null,
  workflowFeatures: {
    ruleExplainability: {
      thresholdResults: null,
      entityToken: null,
      evaluationToken: null,
      dimensions: [],
      thresholdId: null,
      setTagNames: [],
      setOutcome: null,
      isPortfolioEvaluation: false,
      matrixModelResults: {},
    },
    thresholdSimulator: {
      thresholdId: null,
    },
  },
  workflowSnapshots: {
    workflowData: {
      history: {
        initial: null,
        past: [],
      },
    },
    workflowProps: {
      history: {
        initial: null,
      },
    },
  },
  workflowOperationDescriptions: [],
  ui: {
    isInDiffMode: false,
    activeNodeEditorTab: null,
    isTagSwapMode: { isActive: false },
    connector: {
      interactionState: null,
      ownerNodeId: null,
      canCreateItem: true,
    },
    canvasProperties: {
      isMoving: false,
      rect: {
        height: 0,
        width: 0,
        x: 0,
        y: 0,
        zoom: 1,
      },
    },
    editorContainerType: EditorContainerType.PAGE,
    shouldRenderCriticalNodePath: false,
    floatedAnchorNodeId: null,
    focusedNodeId: null,
    highlightedNodeId: null,
    hoveredNodeId: null,
    components: {
      services: {
        serviceRoutingCategoryNameInEditing: null,
        serviceActionOptionPublicIdInEditing: null,
      },
    },
    deactivatedLegacyDataRoutes: [],
    search: {
      isSearchBoxVisible: false,
      matchIndex: 0,
      query: '',
    },
    nodeContainers: {
      inputAttribute: {
        isCollapsed: true,
      },
      snippets: {},
    },
    ruleBuilder: {
      activeRuleNumberDropdown: null,
      isDragging: false,
      highlightedRuleId: null,
      hoveredRuleIndex: null,
      pendingGroup: {
        parentRuleId: null,
        startIndex: null,
      },
      panelBoxContainerWidth: 0,
      activeSubEditor: {
        thresholdId: null,
        type: null,
        side: null,
      },
    },
    expressionBuilder: {
      pendingGroup: { path: null },
    },
    snippet: {
      mode: null,
      activeOperation: {
        operation: null,
        snippetActionPublicId: null,
      },
      preSnippetWorkflowDataSnapshotId: null,
      preSnippetWorkflowPropsSnapshot: null,
      incompatibleActions: [],
    },
  },
  snippet: {
    metadata: null,
  },
  linkedChildAction: {
    createActionPayload: null,
    ownerNodeId: null,
    thresholdIds: [],
  },
  locking: {
    isLocked: false,
    isLoggedInAgentOwner: false,
    ownerEmail: null,
    ownerFirstName: null,
    ownerLastName: null,
  },
  featureFlagsForStore: {
    allowBestEffortRuleEvaluations: false,
    customTransactionAttributes: false,
    dynamicDataRouting: false,
    enableSelfServiceMatchingUi: false,
    enableWorkflowDiff: false,
    useTyphonWorkflowEndpoints: false,
  },
  editHistory: {
    initial: {
      workflowData: null,
      workflowProps: null,
    },
    history: [],
    historyIndex: 0,
  },
} as const;
